<template>
  <div class="geolocation-container">
    <div class="geolocation-wrapper">
      <!-- Header Section -->
      <div class="geolocation-header">
        <h1 class="geolocation-title">Termos de Geolocalização</h1>
        <p class="geolocation-subtitle">
          Autorize o uso da sua localização para melhorar a experiência do
          sistema
        </p>
      </div>

      <!-- Content Card -->
      <div class="geolocation-card">
        <div class="geolocation-content">
          <!-- Icon and intro -->
          <div class="content-header">
            <div class="content-icon">
              <i class="fas fa-globe-americas"></i>
            </div>
            <h2 class="content-title">Uso de Dados de Localização</h2>
          </div>

          <!-- Terms text -->
          <div class="terms-text">
            <div class="text-item">
              <div class="item-bullet"></div>
              <p>
                O uso do sistema pode requerer o acesso e a coleta de dados de
                geolocalização do usuário para o pleno funcionamento de
                determinadas funcionalidades.
              </p>
            </div>

            <div class="text-item">
              <div class="item-bullet"></div>
              <p>
                Ao acessar e utilizar o sistema, você concorda com a coleta, o
                uso e o tratamento dos dados de localização, conforme descrito
                na Política de Privacidade e nos Termos de Geolocalização.
              </p>
            </div>

            <div class="text-item">
              <div class="item-bullet"></div>
              <p>
                Por favor, revise estes termos cuidadosamente antes de utilizar
                funcionalidades que dependam da geolocalização.
              </p>
            </div>

            <div class="text-item">
              <div class="item-bullet"></div>
              <p>
                Caso você não concorde com os termos e condições, não será
                possível utilizar o sistema.
              </p>
            </div>

            <div class="text-item">
              <div class="item-bullet"></div>
              <p>
                O Sistema Clin reserva-se o direito de atualizar os Termos de
                Geolocalização a qualquer momento, a seu exclusivo critério.
                Você deve revisar periodicamente estes termos para estar ciente
                de eventuais atualizações ou modificações.
              </p>
            </div>
          </div>

          <!-- Features section -->
          <div class="features-section">
            <div class="features-header">
              <i class="fas fa-location-arrow"></i>
              <h3>Por que precisamos da sua localização?</h3>
            </div>
            <div class="features-grid">
              <div class="feature-item">
                <div class="feature-icon">
                  <i class="fas fa-shield-alt"></i>
                </div>
                <div class="feature-content">
                  <h4>Segurança</h4>
                  <p>Verificação de acesso seguro ao sistema</p>
                </div>
              </div>
              <div class="feature-item">
                <div class="feature-icon">
                  <i class="fas fa-clock"></i>
                </div>
                <div class="feature-content">
                  <h4>Registro de Atividades</h4>
                  <p>Controle de horários e localização dos cuidados</p>
                </div>
              </div>
              <div class="feature-item">
                <div class="feature-icon">
                  <i class="fas fa-chart-line"></i>
                </div>
                <div class="feature-content">
                  <h4>Relatórios</h4>
                  <p>Geração de relatórios mais precisos e detalhados</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Action buttons -->
          <div class="action-buttons">
            <a
              class="action-btn primary-btn"
              href="https://sistemaclin.com.br/termos-de-uso/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i class="fas fa-external-link-alt"></i>
              <span>Ler Termos de Uso</span>
            </a>

            <a
              class="action-btn secondary-btn"
              href="https://sistemaclin.com.br/politicas-de-privacidade/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i class="fas fa-shield-alt"></i>
              <span>Política de Privacidade</span>
            </a>
          </div>

          <!-- Agreement section -->
          <div class="agreement-section">
            <div class="agreement-card">
              <div class="checkbox-wrapper">
                <b-form-checkbox
                  v-model="accept"
                  name="check-button"
                  class="custom-checkbox"
                >
                  <span class="checkbox-label">
                    Eu autorizo o acesso à minha localização e aceito os
                    <a
                      href="https://sistemaclin.com.br/termos-de-uso/"
                      target="_blank"
                      class="terms-link"
                    >
                      Termos de Uso
                    </a>
                    e a
                    <a
                      href="https://sistemaclin.com.br/politicas-de-privacidade/"
                      target="_blank"
                      class="terms-link"
                    >
                      Política de Privacidade
                    </a>
                  </span>
                </b-form-checkbox>
              </div>
            </div>
          </div>

          <!-- Error messages -->
          <div v-if="errors.length > 0" class="error-section">
            <ErrorsMessages :errors="errors" />
          </div>

          <!-- Save button -->
          <div class="save-section">
            <v-btn
              class="save-btn"
              :class="{ 'btn-disabled': !accept }"
              :disabled="loading || !accept"
              :loading="loading"
              @click="save"
              large
            >
              <i class="fas fa-map-marked-alt save-icon"></i>
              {{ loading ? "Autorizando..." : "Autorizar Localização" }}
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="geolocation-footer">
        <div class="footer-info">
          <i class="fas fa-lock"></i>
          <span>Sua privacidade e localização estão protegidas</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ErrorsMessages from "../Components/ErrorsMessages";
import api from "../../services/api";
import { userData } from "../../lib/functions";

export default {
  name: "TermsGeolocation",
  components: {
    ErrorsMessages,
  },
  data() {
    return {
      accept: false,
      loading: false,
      errors: [],
    };
  },
  methods: {
    async save() {
      try {
        this.loading = true;
        let { accept } = this;

        if (!accept) {
          this.loading = false;
          this.errors = ["Aceite os termos"];
          return;
        }

        const response = await api.post("/rest-home/user/terms-geolocation");
        const { error, errors } = response.data;

        if (error) {
          this.loading = false;
          this.errors = errors;
        } else {
          let { user } = userData();
          user.terms_geolocation = 1;
          let newUser = user;
          sessionStorage.setItem("user", JSON.stringify(newUser));
          location.reload();
          this.errors = [];
        }
      } catch (error) {
        this.loading = false;
        console.log(error);
      }
    },
  },
};
</script>

<style scoped>
.geolocation-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.geolocation-wrapper {
  width: 100%;
  max-width: 700px;
  margin: 0 auto;
}

/* Header Section */
.geolocation-header {
  text-align: center;
  margin-bottom: 32px;
}

.geolocation-title {
  font-size: 32px;
  font-weight: 700;
  color: white;
  margin: 0 0 12px 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.geolocation-subtitle {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  font-weight: 400;
}

/* Content Card */
.geolocation-card {
  background: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.geolocation-content {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

/* Content Header */
.content-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding-bottom: 20px;
  border-bottom: 2px solid #f1f5f9;
}

.content-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 12px;
  color: white;
  font-size: 20px;
}

.content-title {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

/* Terms Text */
.terms-text {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.text-item {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.item-bullet {
  width: 8px;
  height: 8px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 50%;
  margin-top: 8px;
  flex-shrink: 0;
}

.text-item p {
  margin: 0;
  font-size: 16px;
  line-height: 1.6;
  color: #475569;
}

/* Features Section */
.features-section {
  background: #f8fafc;
  border-radius: 16px;
  padding: 24px;
  border: 1px solid #e2e8f0;
}

.features-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e2e8f0;
}

.features-header i {
  font-size: 20px;
  color: #10b981;
}

.features-header h3 {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
}

.feature-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  transition: all 0.3s ease;
}

.feature-item:hover {
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.1);
  border-color: #10b981;
}

.feature-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  border-radius: 10px;
  color: #059669;
  font-size: 16px;
  flex-shrink: 0;
}

.feature-content h4 {
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 4px 0;
}

.feature-content p {
  font-size: 13px;
  color: #64748b;
  margin: 0;
  line-height: 1.4;
}

/* Action Buttons */
.action-buttons {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 16px 24px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 14px;
  text-decoration: none;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.primary-btn {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.primary-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(16, 185, 129, 0.4);
  color: white;
  text-decoration: none;
}

.secondary-btn {
  background: white;
  color: #64748b;
  border-color: #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.secondary-btn:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
  transform: translateY(-2px);
  color: #475569;
  text-decoration: none;
}

/* Agreement Section */
.agreement-section {
  margin: 16px 0;
}

.agreement-card {
  background: #f0fdf4;
  border: 2px solid #bbf7d0;
  border-radius: 12px;
  padding: 24px;
  transition: all 0.3s ease;
}

.agreement-card:hover {
  border-color: #10b981;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.1);
}

.checkbox-wrapper {
  display: flex;
  align-items: flex-start;
}

.checkbox-label {
  font-size: 16px;
  line-height: 1.6;
  color: #374151;
  margin-left: 12px;
}

.terms-link {
  color: #10b981;
  font-weight: 600;
  text-decoration: none;
}

.terms-link:hover {
  color: #059669;
  text-decoration: underline;
}

/* Custom Checkbox */
.custom-checkbox
  >>> .custom-control-input:checked
  ~ .custom-control-label::before {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-color: #10b981;
}

.custom-checkbox >>> .custom-control-label::before {
  border-radius: 6px;
  border: 2px solid #d1d5db;
  width: 24px;
  height: 24px;
}

.custom-checkbox >>> .custom-control-label::after {
  width: 24px;
  height: 24px;
}

/* Error Section */
.error-section {
  margin: -16px 0 0 0;
}

/* Save Section */
.save-section {
  display: flex;
  justify-content: center;
}

.save-btn {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 12px !important;
  padding: 16px 48px !important;
  font-size: 16px !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3) !important;
  transition: all 0.3s ease !important;
  min-width: 280px !important;
}

.save-btn:not(.btn-disabled):hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 8px 20px rgba(59, 130, 246, 0.4) !important;
}

.btn-disabled {
  background: #9ca3af !important;
  box-shadow: none !important;
  cursor: not-allowed !important;
}

.save-icon {
  margin-right: 8px;
}

/* Footer */
.geolocation-footer {
  text-align: center;
  margin-top: 32px;
}

.footer-info {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 25px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  font-weight: 500;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

/* Responsive Design */
@media (max-width: 768px) {
  .geolocation-container {
    padding: 16px;
  }

  .geolocation-card {
    padding: 24px;
  }

  .content-header {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }

  .action-buttons {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .action-btn {
    padding: 14px 20px;
    font-size: 13px;
  }

  .geolocation-title {
    font-size: 24px;
  }

  .features-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}

@media (max-width: 480px) {
  .text-item {
    flex-direction: column;
    gap: 8px;
    align-items: flex-start;
  }

  .item-bullet {
    margin-top: 0;
  }

  .save-btn {
    min-width: 100% !important;
    padding: 16px 24px !important;
  }

  .checkbox-label {
    font-size: 14px;
  }

  .feature-item {
    flex-direction: column;
    text-align: center;
    gap: 8px;
  }

  .feature-icon {
    align-self: center;
  }
}

/* Loading Animation */
@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.save-btn[loading] {
  animation: pulse 1.5s ease-in-out infinite;
}
</style>